<template>
  <div>
    <p class="my-4">
        <a v-if="filter_for" @click="$emit('clear_filter')" class="badge badge-secondary mr-3">{{ $t("filtering for") }} {{ local_filter }} <i class="fal fa-times ml-2"></i></a>
    </p>    
    <div v-if="aggregate_by == 'service_area' && local_filter" class="custom-control custom-switch mb-0">
      <input type="checkbox" class="custom-control-input" id="sort-by-rank" v-model="sort_by_rank">
      <label class="custom-control-label" for="sort-by-rank">{{ $t('prioritize by Service Area') }}</label>
    </div>
    <div class="row mt-5">
				<div class="col-md-6 mb-4 mb-md-0">
          <h3 class="mt-0">{{ $t('total capital cost' + budgetType) }}</h3> 
          <aggregate-chart v-if="chartRecords.length" :records="chartRecords" v-on:chartClick="handleChartClick"></aggregate-chart>
          <div v-if="aggregate_by == 'lifecycle_activity_driver'" v-html="$t('lifecycle-activity-description')">
          </div>
        </div>
				<div class="col-md-6">
					<table class="table table-striped table-hover table-sm small">
						<thead>
							<tr>
								<th>{{ (local_filter) ? $t('Investment name') : $t('aggregates.' + aggregate_by )}}</th>
								<th>{{ $t('capital cost' + budgetType) }}</th>
                <th>{{ $t('Amount funded') }}</th>
                <th>{{ $t('Amount unfunded') }}</th>
							</tr>
            </thead>
            <tbody>
              <tr v-for="(row,index) in chartRecords" :key="index">
                <td><a @click="handleChartClick(row.name)">{{ row.name }}<span v-if="!local_filter"> ({{ row.count }})</span></a>
                  <strong v-if="aggregate_by == 'lifecycle_activity_driver' && row.name == 'Operations & Maintenance'"> *</strong>
                </td>
                <td>{{ row.capital_cost | formatMoney }}</td>
                <td>{{ row.funded_amount | formatMoney }}</td>
                <td>{{ row.unfunded_amount | formatMoney }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="aggregate_by == 'lifecycle_activity_driver'">
            <p>
              <strong>*</strong> {{ $t('operations-and-maintenance') }}
            </p>
            <p>{{ $t('disposal-amount') }} {{ total_amount_disposal | formatMoney }}.</p>
          </div>
          <TotalData :chartRecords="chartRecords" :budgetType="budgetType"></TotalData>
        </div>
    </div>
    <div class="row mt-4">
      <div class="col-md-8 mb-4 mb-md-0">
        <h3>{{ $t(budgetType) }}</h3>
        <cashflow-chart v-if="cashflowRecords.length" :records="cashflowRecords"></cashflow-chart>
      </div>
      <div class="col-md-4 mb-4 mb-md-0" v-if="showSpeedometer">
        <h3>{{ $t('Infrastructure funding level') }}</h3>
        <p>{{ $t('Planned capital investments') }}</p>
        <SpeedometerChart :funded="total_amount_funded" :budgetType="budgetType" :unfunded="total_amount_unfunded"></SpeedometerChart>
     </div>
    </div>
  </div>
</template>

<script>
import OpenData from '../libraries/OpenData';
import AggregateChart from './AggregateChart.vue';
import CashflowChart from './CashflowChart.vue';
import TotalData from './TotalsData.vue';
import SpeedometerChart from './SpeedometerChart.vue';

export default {
  props: ['aggregate_by', 'wheres', 'filter_for','cashflow_years','budgetType','totalInvestmentNeeds'],
  components: {
    AggregateChart, TotalData, CashflowChart, SpeedometerChart
  },  

  data() {
    return {
      old_aggregate_by: this.aggregate_by, //this will be used for the slight time delay from fetching new results
      records: [],
      cashflow_records: [],
      group_bys: ['department','service_area','investment_type','spap_theme','lifecycle_activity_driver','ward'],
      loading: false,
      default_sort: {
        column: 'sum_funded',
        direction: 'DESC',
      },
      timeout: null,
      timeout_time: 100,
      cashflow_timeout: null,
      local_filter: this.filter_for,
      sort_by_rank: false,
    }
  },

  async created() {
    this.fetch();
    this.fetch_cashflows();
  },
  
  watch: {
    parameters() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => {
        this.fetch();
      },this.timeout_time);
    },

    cashflow_parameters() {
      if (this.cashflow_timeout) {
        clearTimeout(this.cashflow_timeout);
      }
      this.cashflow_timeout = setTimeout(() => {
        this.fetch_cashflows();
      },this.timeout_time);
    },

    aggregate_by(newValue,oldValue) {
      this.old_aggregate_by = oldValue;
      this.$emit('filter_update',null);
    },

    filter_for(newValue) {
      this.local_filter = newValue;
    },
  },

  methods: {
    async fetch() {
      const params = this.parameters;
      this.loading = true;
      const data = await OpenData.getResults(this.$OPEN_DATA_API,params);
      this.records = data;
      this.old_aggregate_by = this.aggregate_by;
 
      this.loading = false;
    },

    async fetch_cashflows() {
      this.cashflow_records = await OpenData.getResults(this.$OPEN_DATA_API,this.cashflow_parameters);
    },

    handleChartClick(filter_value) {
      if (this.local_filter) {
        //have already drilled down, now emit a modal with the information
        this.$emit('modal',this.records.find(row => row.investment_name === filter_value));
      } else {
        const val = (filter_value === this.$t('citywide')) ? "" : filter_value;
        this.local_filter = filter_value;
        this.$emit('aggregate_update',this.aggregate_by);
        this.$emit('filter_update',val);
      }
    },

  }, 

  computed: {
    showSpeedometer() {
      return (this.aggregate_by === 'department' || this.aggregate_by === 'service_area') && this.totalInvestmentNeeds;
    },

    cashflowRecords() {
      let rows = [];
      if (this.cashflow_years.start && this.cashflow_years.end) {
        for (let i = parseInt(this.cashflow_years.start); i <= parseInt(this.cashflow_years.end); i++) {
          rows.push({
            funding_year: i,
            sum_funded: "0.0",
            sum_unfunded: "0.0",
          })
        }
        this.cashflow_records.forEach(row => {
          let idx = parseInt(row.funding_year) - parseInt(this.cashflow_years.start);
          if (idx < rows.length) {
            rows[idx].sum_funded = row.sum_funded;
            rows[idx].sum_unfunded = row.sum_unfunded;
          }
        })
      }
      return rows;
    },

    chartRecords() {
      return this.records.map(row => {
        return {
          name: row.investment_name || row[this.old_aggregate_by] || this.$t("citywide"),
          //capital_cost: (row.sum_capital_cost) ? row.sum_capital_cost : parseInt(row.sum_funded || 0) + parseInt(row.sum_unfunded || 0),
          capital_cost: parseInt(row.sum_funded || 0) + parseInt(row.sum_unfunded || 0),
          funded_amount: row.sum_funded || 0,
          unfunded_amount: row.sum_unfunded || 0,
          count: row.count_distinct_investment_name,
          disposal_amount: row.sum_disposal || 0,
          rank: row.rank,
        }
      }).sort((a,b) => {
        //other just gotta be at the bottom apparently
        if (this.aggregate_by==="spap_theme" && b.name === "Other") {
          return -1;
        }
        if (this.local_filter && this.aggregate_by === 'service_area' && this.sort_by_rank && a.rank && b.rank) {
          return a.rank - b.rank;
        }
        return b.capital_cost - a.capital_cost
      });

    },
    cashflow_parameters() {
      let group_parameters = {
        select: ['funding_year'],
        function_column: ['funded','unfunded'],
        function: ['sum','sum'],
        group: ['funding_year'],
      };
      let order_parameters = [
        {key: 'funding_year', direction: 'ASC'}
      ];
      let where_parameters = this.wheres;
      return { group_parameters, order_parameters, where_parameters }
    },

    parameters() {
      let where_parameters = this.wheres;
      let group_parameters = this.group_parameters;
      return { where_parameters, group_parameters, };
    },
    
    order_parameters() {
      if (this.local_filter && this.aggregate_by === 'service_area' && this.sort_by_rank) {
        return [{key: 'rank', direction: 'ASC' }];
      }
      return [{key: this.default_sort.column, direction: this.default_sort.direction }];
    },

    total_amount_funded() {
      return this.chartRecords.reduce((total,val) => {
        return total + parseFloat(val.funded_amount);
      },0);
    },

    total_amount_unfunded() {
      return this.chartRecords.reduce((total,val) => {
        return total + parseFloat(val.unfunded_amount);
      },0);
    },

    total_amount_disposal() {
      return this.chartRecords.reduce((total,val) => {
        return total + parseFloat(val.disposal_amount);
      },0);
    },

    group_parameters() {
      if (this.local_filter) {
        return {
          select: ['investment_name','department','service_area','investment_type','spap_theme','lifecycle_activity_driver','class_estimate','ward', 'location', 'project_details','rank'],
          function_column: ['capital_cost','funded','unfunded','distinct investment_name',
                            'previously_approved_project_funding','disposal', 
                            'preliminary_capital_cost_6_year','preliminary_capital_cost_10_year',
                            'preliminary_funded_6_year','preliminary_funded_10_year',
                            'preliminary_unfunded_6_year','preliminary_unfunded_10_year'],
          function: [ 'sum','sum','sum','count','sum','sum',
                      'sum','sum','sum','sum','sum','sum'],
          group: ['investment_name','department','service_area','investment_type','spap_theme','lifecycle_activity_driver','class_estimate','ward', 'location', 'project_details','rank']
        }
      } else {
        return {
          select: [this.aggregate_by],
          function_column: ['capital_cost','funded','unfunded','distinct investment_name','disposal',
                            'preliminary_capital_cost_6_year','preliminary_capital_cost_10_year',
                            'preliminary_funded_6_year','preliminary_funded_10_year',
                            'preliminary_unfunded_6_year','preliminary_unfunded_10_year'],
          function: ['sum','sum','sum','count','sum',
                      'sum','sum','sum','sum','sum','sum'],
          group: [this.aggregate_by]
        }
      }
    }
  }
}
</script>

<style>
a:hover {
  cursor: pointer;
}
</style>